import { Link as RouterLink } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';

import Button from 'components/partials/button/button';

import Icons from 'assets/icons';

export interface ReportRow {
  id: string | number;
  title?: string;
  description?: string;
  productId?: string | number | null;
  [key: string]: unknown;
}

export interface ProductOption {
  id: string | number;
  name?: string;
}

const commonStyles = 'pl-4 border-r-2 border-white font-semibold';

export const getReportsTableSettings = (
  products: ProductOption[]
): ColumnDef<ReportRow>[] => [
  {
    id: 'title',
    accessorKey: 'title',
    header: () => <span>REPORT NAME</span>,
    cell: (info) => info.renderValue(),
    headerClassName: `${commonStyles} cursor-pointer rounded-tl-md rounded-bl-md`,
    className: 'pl-4 tablet:w-[25%] sm:w-[25%] font-semibold',
    enableSorting: true,
    sortingFn: 'alphanumeric',
  } as ColumnDef<ReportRow>,
  {
    id: 'description',
    accessorKey: 'description',
    header: () => <span>DESCRIPTION</span>,
    cell: (info) => <span>{info.renderValue() as React.ReactNode}</span>,
    headerClassName: `${commonStyles} cursor-context-menu`,
    className: 'pl-4 tablet:w-[30%] sm:w-[35%] lg:w-[40%]',
    enableSorting: false,
  } as ColumnDef<ReportRow>,
  {
    id: 'productId',
    accessorKey: 'productId',
    header: () => <span>ASSOCIATED PRODUCT</span>,
    cell: (info) => {
      const productId = info.getValue() as string | number | undefined;

      const product = products?.find((p) => p.id === productId);

      return product ? (
        <RouterLink to={`/admin/products/${productId}`}>
          <span className="text-blue-700 underline">{product.name}</span>
        </RouterLink>
      ) : (
        <span className="text-gray-500">Not assigned</span>
      );
    },
    headerClassName: `${commonStyles} cursor-pointer`,
    className: 'pl-4 tablet:w-[25%] sm:w-[20%]',
    enableSorting: true,
  } as ColumnDef<ReportRow>,

  {
    id: 'button-cell',
    cell: ({
      row: {
        original: { id },
      },
    }) => (
      <RouterLink
        to={`/admin/reports/${id}`}
        data-testid="view-report-details-button"
      >
        <Button
          variant="reserve-outlined"
          size="sm"
          iconBefore={<Icons.Eye aria-label="EyeIcon" />}
          className="text-xs md:text-sm"
        >
          View Details
        </Button>
      </RouterLink>
    ),
    headerClassName: 'h-12 rounded-tr-md rounded-br-md',
    className: 'py-3 pl-4',
  } as ColumnDef<ReportRow>,
];
